import { Injectable } from "@angular/core";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { map, mergeMap, tap } from "rxjs/operators";
import { Configuration, Department, InsightConfigurationType, KpiList} from "src/app/core/models/insight-configuration.model";
import { InsightConfigurationService } from "src/app/core/services/insight-configuration.service";
import { GetConfigurationTypes, SetSelectedConfigType, GetConfigurationList, DeleteConfiguration, GetUserAccess, GetPlantList, SetSelectedPlant, GetRolesAndDepartmentByAsset } from "./insight-configuration.action";
import { KPIConfigurationState } from "./kpi-configuration/kpi-configuration.state";
import { PredictiveFailureState } from "./predictive-failure-configuration/predictive-failure-configuration.state";
import moment from "moment";
import _ from "lodash";
import { EwbConfigurationState } from "./ewb-configuration/ewb-configuration.state";

/* istanbul ignore file */
export class ConfigurationStateModel{
  configurationTypes: InsightConfigurationType[];
  kpiListSelection: KpiList[];
  selectedConfigType: InsightConfigurationType;
  insightsConfigurationsList: Configuration[];
  userHasAccess: boolean;
  accessConfig: boolean;
  plantList: any[];
  selectedPlant: any;
  plantsLoaded: boolean;
  departmentList: Department[];
  departmentRoleList: any[];
}
  
@State<ConfigurationStateModel>({
  name: 'configurations',
  defaults: {
    plantList: [],
    selectedPlant: null,
    plantsLoaded: false,
    configurationTypes: [],
    kpiListSelection: [],
    selectedConfigType: null,
    insightsConfigurationsList: [],
    userHasAccess : null,
    accessConfig: true,
    departmentList: [],
    departmentRoleList: []
  },
  children: [KPIConfigurationState, PredictiveFailureState,EwbConfigurationState]
})
@Injectable()
export class ConfigurationState {

  constructor(
    private insightConfigurationService: InsightConfigurationService
  ) {}

  @Selector()
  public static userHasAccess(state: any) {
    return state.userHasAccess
  }

  @Action(GetConfigurationTypes, {cancelUncompleted: true})
  getConfigurationTypes(ctx: StateContext<ConfigurationStateModel>) {
    return this.insightConfigurationService.getConfigurationTypes()
    .pipe(
      tap(configurationType => {
        ctx.patchState({ 
          configurationTypes: [...configurationType]
        })
      })
    )
  }

  @Action(SetSelectedConfigType, {cancelUncompleted: true})
  setSelectedConfigType(ctx: StateContext<ConfigurationStateModel>, {selectedType}: SetSelectedConfigType) {
    ctx.patchState({
      selectedConfigType : selectedType
    })
  }

  @Action(GetConfigurationList, {cancelUncompleted: true})
  getConfigurationList(ctx: StateContext<ConfigurationStateModel>, {plantId}: GetConfigurationList) {
    return this.insightConfigurationService.getConfigurationList(plantId)
    .pipe(
      tap(configurationItem => {
        let newConfiguration = configurationItem.map(configuration => {
          configuration.createdDate = moment.utc(configuration.createdDate).toISOString();
          return configuration;
        })
        ctx.patchState({
          insightsConfigurationsList: [...newConfiguration]
        })
      })
    )
  }

  @Action(GetUserAccess)
  setAccess(ctx: StateContext<ConfigurationStateModel>) {

    return this.insightConfigurationService.getUserRoles()
    .pipe(
      tap(roles => {
        ctx.patchState({
          userHasAccess: roles.includes('Admin_IA')
        })
      })
    )
  }
  
  @Action(GetPlantList, {cancelUncompleted: true})
  getPlantList(ctx: StateContext<ConfigurationStateModel>) {
    return this.insightConfigurationService.getPlantList().pipe(
      tap(plant => {
        let newPlant = plant.map(asset => {
          let newPlants = {
            plantId: asset['PlantId'],
            plantName: asset['PlantName']
          }
          return newPlants;
        })

        let sortedPlant = _.sortBy(newPlant, 'plantName'); 
        ctx.patchState({
          plantList: [...sortedPlant, {plantId:'Multi-Plant', plantName: 'Multi-Plant'}]
        })
      })
    );
  }

  @Action(SetSelectedPlant, {cancelUncompleted: true})
  setSelectedPlant(ctx: StateContext<ConfigurationStateModel>, {plant}: SetSelectedPlant) {
    ctx.patchState({
      selectedPlant: plant,
      plantsLoaded: true
    })
    
    ctx.dispatch(new GetConfigurationList(plant.plantId))
  }
  
  @Action(DeleteConfiguration)
  deleteConfiguration(ctx: StateContext<ConfigurationStateModel>,{id}: DeleteConfiguration) {
    return this.insightConfigurationService.deleteConfiguration(id);
  }

  @Action(GetRolesAndDepartmentByAsset, {cancelUncompleted: true})
  getRolesAndDepartmentByAsset(ctx: StateContext<ConfigurationStateModel>, {assetId}: GetRolesAndDepartmentByAsset) {
    ctx.patchState({
      departmentList: [],
      departmentRoleList: []
    })
    return this.insightConfigurationService.getRoleIds(assetId)
    .pipe(
      map(roleIds => roleIds),
      mergeMap((roleIds) => {
        return this.insightConfigurationService.getRolesByPlant(roleIds)
        .pipe(
          tap(roles => {
            this.rolesByDepartment(roles,ctx);
          })
        )
      })
    )
  }
  public rolesByDepartment(roles: any[],ctx : StateContext<ConfigurationStateModel>) {
    let departmentList = [];
    let departmentRoleList = [];

    // Map departments into list
    roles.forEach(role => {
      departmentList = [...departmentList, ...role['Departments'].map(roleDept => ({
        departmentId: roleDept['DepartmentId'],
        departmentName: roleDept['DepartmentName']
      }))]
    })

    //Remove department duplicates
    departmentList = _.uniqBy(departmentList, 'departmentId');

    // Build roles categorized based on per department
    departmentRoleList = departmentList.map(dept => {
      let deptRoles = [];
      deptRoles = roles.filter(role => 
          !!role['Departments'].find(r => r['DepartmentId'].toUpperCase() === dept.departmentId.toUpperCase())
        ).map(role => ({
          roleId: role['RoleId'].toUpperCase(),
          roleName: role['RoleName']
        }))
        return {
          departmentId: dept.departmentId,
          roles: deptRoles
        }
    })

    // Save to state the departmentList and Role list based on plant/asset selection
    ctx.patchState({
      departmentList: departmentList,
      departmentRoleList: departmentRoleList
    })
  }
}
